import { useState, useRef, useCallback, useMemo } from "react";
import Typography from "../Typography";
import Button from "../Button/Button";
import { RiCloseLine } from "react-icons/ri";
import {
  CLIENT_ATTACHMENT_ACCEPTED_TYPES,
  CLIENT_ATTACHMENT_EXTENSIONS,
  parseAcceptedExtensions,
  validateUploadFile,
  getFileExtension,
} from "@/utils/fileUploadValidation";

function validateFileSize(file, maxSizeMB) {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `File "${file.name}" exceeds ${maxSizeMB}MB limit`;
  }
  return null;
}

const FileDropzone = ({
  onFilesChange,
  acceptedTypes = CLIENT_ATTACHMENT_ACCEPTED_TYPES,
  maxFiles = 10,
  maxSizeMB = 10,
  label = "Attachments",
  className = "",
  /** When false, validate extension/MIME only (e.g. task attachments — PDFs false-positive on content scan). */
  inspectContent = true,
}) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef(null);

  const allowedExtensions = useMemo(
    () =>
      acceptedTypes === "*"
        ? null
        : (parseAcceptedExtensions(acceptedTypes) ?? [...CLIENT_ATTACHMENT_EXTENSIONS]),
    [acceptedTypes]
  );

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFiles = useCallback(
    async (newFiles) => {
      setError("");
      const fileArray = Array.from(newFiles);

      if (files.length + fileArray.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const validFiles = [];
      setIsValidating(true);
      try {
        for (const file of fileArray) {
          const sizeError = validateFileSize(file, maxSizeMB);
          if (sizeError) {
            setError(sizeError);
            return;
          }

          if (allowedExtensions) {
            const securityError = await validateUploadFile(file, {
              allowedExtensions,
              inspectContent,
            });
            if (securityError) {
              setError(securityError);
              return;
            }
          }

          const isDuplicate = files.some((f) => f.name === file.name && f.size === file.size);
          if (!isDuplicate) {
            validFiles.push(file);
          }
        }

        const updatedFiles = [...files, ...validFiles];
        setFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
      } finally {
        setIsValidating(false);
      }
    },
    [files, maxFiles, maxSizeMB, onFilesChange, allowedExtensions, inspectContent]
  );

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
    setError("");
  };

  const getFileIcon = (fileName) => {
    const extension = getFileExtension(fileName) ?? "";
    const iconMap = {
      pdf: "ri-file-pdf-line text-red-500",
      doc: "ri-file-word-line text-blue-500",
      docx: "ri-file-word-line text-blue-500",
      xls: "ri-file-excel-line text-green-500",
      xlsx: "ri-file-excel-line text-green-500",
      png: "ri-image-line text-purple-500",
      jpg: "ri-image-line text-purple-500",
      jpeg: "ri-image-line text-purple-500",
      gif: "ri-image-line text-purple-500",
      zip: "ri-file-zip-line text-yellow-600",
      rar: "ri-file-zip-line text-yellow-600",
      txt: "ri-file-text-line text-gray-500",
    };
    return iconMap[extension] || "ri-file-line text-gray-500";
  };

  return (
    <div className={className}>
      {label && (
        <Typography
          variant="text2"
          component="label"
          className="font-medium text-text-primary mb-1"
        >
          {label}
        </Typography>
      )}

      {/* Dropzone Area */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={isValidating ? undefined : handleBrowseClick}
        className={`
          relative border-2 border-dashed rounded-md p-6 text-center
          transition-all duration-200 ease-in-out
          ${isValidating ? "cursor-wait opacity-70" : "cursor-pointer"}
          ${
            isDragging
              ? "border-brand-primary bg-brand-primary/5"
              : "border-border-primary hover:border-brand-primary hover:bg-gray-50"
          }
          ${error ? "border-red-400" : ""}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={handleFileInputChange}
          disabled={isValidating}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-2">
          <div
            className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isDragging ? "bg-brand-primary/10" : "bg-gray-100"}
          `}
          >
            <i
              className={`ri-upload-cloud-2-line text-2xl ${
                isDragging ? "text-brand-primary" : "text-gray-400"
              }`}
            />
          </div>

          <div>
            <Typography variant="text2" className="text-text-primary">
              {isValidating ? (
                <span className="text-brand-primary font-medium">Validating files…</span>
              ) : isDragging ? (
                <span className="text-brand-primary font-medium">Drop files here</span>
              ) : (
                <>
                  Drag & drop files here, or{" "}
                  <span className="text-brand-primary font-medium underline">browse</span>
                </>
              )}
            </Typography>
          </div>

          <Typography variant="text2" className="text-text-secondary text-xs">
            Max {maxFiles} files, up to {maxSizeMB}MB each
          </Typography>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-sm text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line" />
          {error}
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between p-2 bg-gray-50 border border-border-primary rounded-md group"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <i className={`${getFileIcon(file.name)} text-lg`} />
                <div className="min-w-0 flex-1">
                  <Typography
                    variant="text2"
                    className="text-text-primary truncate block"
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="text2" className="text-text-secondary text-xs">
                    {formatFileSize(file.size)}
                  </Typography>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close modal"
              >
                <RiCloseLine className="w-5 h-5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
